import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import { api } from "../api/client";
import type { Exercise, NamedPlace } from "../api/types";
import { useUi } from "../state/ui";
import { fromExercise, type Draft } from "../views/missionDraft";
import { displayedDraft } from "./terrain/terrainWeather";
import { placeZoomFor } from "./zones/placeSearch";
import { useZoneDraw } from "./zones/zoneDraw";

/** Centrage de la carte sur le lieu nommé d'un nouveau terrain à l'ouverture de la vue Manœuvre (demande d'Alexis du
 *  01/11/2026 : « lorsque le menu manœuvre s'ouvre, connaissant déjà la localité renseignée dans le menu données
 *  initiales, je veux que la carte se centre automatiquement sur la localité »). Déplacement de vue seulement : ni
 *  repère, ni couche, ni outil (carte gelée).
 *
 *  Un seul point de décision, dans la carte (`MapCanvas`), et non plus dans le bloc « Terrain libre » de la vue :
 *  - chaque ouverture de la vue (barre latérale, « Créer et placer le terrain », phrase « Ensuite », vue restaurée au
 *    rechargement) et chaque changement du lieu pendant qu'elle est ouverte ; jamais à chaque rendu, ni au retour du
 *    focus, ni quand le dossier est relu ;
 *  - le lieu du dossier enregistré (`parameters.place`) ou celui du brouillon des Données initiales gardé sur ce poste,
 *    pas encore enregistré (`displayedDraft`, la source de la carte et de la météo du terrain) ;
 *  - jamais avec un axe tracé ou en cours de tracé, ni en CENTAC ;
 *  - appliqué quand la carte est chargée (demande gardée jusque-là), quel que soit le moteur (Mapbox ou MapLibre) ;
 *  - les cadrages automatiques (camp, terrain ouvert, barycentre des unités, `recenter.ts`) qui arriveraient ensuite
 *    pour la même carte, tant que la vue reste ouverte, cèdent la place (`held`) : ils l'écrasaient.
 *  L'utilisateur garde la main : un seul déplacement, rien ne le refait tant que la vue reste ouverte sur le même
 *  lieu. */

/** Lieu à rejoindre : centre et zoom du niveau (`PLACE_ZOOM`). */
export interface PlaceTarget {
  center: [number, number];
  zoom: number;
}

/** Lieu nommé qui attend son terrain : celui du brouillon gardé sur ce poste s'il y en a un (c'est lui que les
 *  Données initiales affichent), sinon celui du dossier enregistré ; null en CENTAC ou sans lieu. */
export function awaitedPlace(
  exercise: Pick<Exercise, "id" | "version" | "name" | "language" | "map_slug" | "parameters"> | undefined,
  draft: Pick<Draft, "parameters"> | null,
): (PlaceTarget & { key: string }) | null {
  const parameters = draft?.parameters ?? (exercise ? fromExercise(exercise).parameters : null);
  if (!parameters || parameters.level === "CENTAC") return null;
  const place: NamedPlace | null | undefined = parameters.place;
  if (!place || !Number.isFinite(place.lon) || !Number.isFinite(place.lat)) return null;
  const zoom = placeZoomFor(parameters.level);
  return { center: [place.lon, place.lat], zoom, key: `${place.lon},${place.lat}|${zoom}` };
}

/** Mémoire de la décision : la vue est-elle ouverte (depuis la dernière décision), et quel lieu y a déjà été traité
 *  (rejoint, ou écarté pour cause d'axe) pendant cette ouverture. */
export interface PlaceFramingMemory {
  open: boolean;
  seen: string | null;
}

export const PLACE_FRAMING_CLOSED: PlaceFramingMemory = { open: false, seen: null };

export interface PlaceFramingInput {
  /** Vue Manœuvre affichée. */
  manoeuvre: boolean;
  /** Carte chargée : tant qu'elle ne l'est pas, rien n'est décidé (l'ouverture reste à traiter). */
  ready: boolean;
  exerciseId: string | null;
  place: (PlaceTarget & { key: string }) | null;
  /** Axe du nouveau terrain tracé ou en cours de tracé. */
  axis: boolean;
}

/** Décision pure : faut-il rejoindre le lieu maintenant ? Rend la mémoire suivante et la cible, ou null. Une ouverture
 *  de la vue oublie le lieu déjà traité ; un lieu traité ne l'est plus jusqu'à la prochaine ouverture ou jusqu'à ce
 *  qu'il change. */
export function placeFramingStep(memory: PlaceFramingMemory, input: PlaceFramingInput): { memory: PlaceFramingMemory; frame: PlaceTarget | null } {
  if (!input.manoeuvre) return { memory: PLACE_FRAMING_CLOSED, frame: null };
  if (!input.ready) return { memory, frame: null };
  const seen = memory.open ? memory.seen : null;
  const key = input.place ? `${input.exerciseId ?? "new"}|${input.place.key}` : null;
  if (!key || key === seen) return { memory: { open: true, seen: key ?? seen }, frame: null };
  // Axe déjà tracé (ou en cours) : le lieu est écarté pour cette ouverture, le tracé n'est jamais déplacé.
  if (input.axis) return { memory: { open: true, seen: key }, frame: null };
  return { memory: { open: true, seen: key }, frame: { center: input.place!.center, zoom: input.place!.zoom } };
}

/** Branchement dans la carte : `fly` déplace la vue (marges des panneaux comprises, à la charge de la carte), `ready`
 *  dit que la carte est chargée. Rend `held()` : vrai tant qu'un lieu rejoint tient la vue — la vue Manœuvre reste
 *  ouverte sur la même carte —, les cadrages automatiques s'abstiennent alors. */
export function usePlaceFraming(ready: boolean, fly: (target: PlaceTarget) => void): () => boolean {
  const view = useUi((s) => s.view);
  const exerciseId = useUi((s) => s.exerciseId);
  const { data: exercises } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises?.find((e) => e.id === exerciseId);
  const saved = exercise?.parameters.place;
  const savedKey = exercise && saved ? `${exercise.parameters.level}|${saved.lon},${saved.lat}` : null;
  // Dossier arrivé (liste relue au chargement) : son brouillon se lit enfin, même sans lieu enregistré.
  const loaded = !exerciseId || Boolean(exercise);
  const axis = useZoneDraw((s) => s.axis !== null || s.drawing?.mode === "axis");
  const memory = useRef<PlaceFramingMemory>(PLACE_FRAMING_CLOSED);
  // Carte sur laquelle le lieu a été rejoint (undefined : aucun lieu ne tient la vue).
  const hold = useRef<string | null | undefined>(undefined);
  const flyRef = useRef(fly);
  flyRef.current = fly;
  const manoeuvre = view === "manoeuvre";

  useEffect(() => {
    // Brouillon lu au moment de la décision seulement (ouverture, lieu changé) : jamais à chaque rendu de la carte.
    const place = manoeuvre ? awaitedPlace(exercise, displayedDraft(exerciseId, exercise)) : null;
    const step = placeFramingStep(memory.current, { manoeuvre, ready, exerciseId, place, axis });
    memory.current = step.memory;
    if (!step.memory.open || (ready && !place)) hold.current = undefined;
    if (!step.frame) return;
    hold.current = useUi.getState().mapSlug;
    flyRef.current(step.frame);
  }, [manoeuvre, ready, exerciseId, loaded, savedKey, axis]); // eslint-disable-line react-hooks/exhaustive-deps

  return useCallback(() => {
    const ui = useUi.getState();
    return hold.current !== undefined && ui.view === "manoeuvre" && hold.current === ui.mapSlug;
  }, []);
}
